import React from "react";
import { MessageSquare, Mail, Phone, Mic, AlertTriangle } from "lucide-react";
import { useT } from "@/i18n";
import { StatusChip } from "@/components/common/StatusChip";
import { EmptyState } from "@/components/common/EmptyState";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const CHANNEL_ICON = { whatsapp: MessageSquare, sms: Phone, email: Mail, voz: Mic };
const STATUS_TONE = {
  sin_procesar: "gray", pendiente: "amber", confirmado: "green",
  descartado: "gray", no_accionable: "gray", agrupado: "blue",
  respuesta_sub: "blue",   // Fix O
};

const relativeTime = (iso) => {
  if (!iso) return "—";
  const diffMin = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return "ahora";
  if (diffMin < 60) return `hace ${diffMin} min`;
  const h = Math.round(diffMin / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} d`;
};

export const IntakeMessageList = ({
  items, contadores, filters, onFiltersChange, selectedId, onSelect, loading,
}) => {
  const { t } = useT();
  const estados = ["pendiente", "sin_procesar", "confirmado", "descartado", "no_accionable", "agrupado",
    "respuesta_sub"];   // Fix O

  return (
    <div className="flex h-full w-[380px] shrink-0 flex-col border-r border-line bg-white">
      <div className="space-y-2 border-b border-line p-3">
        <Input
          data-testid="intake-search-input"
          value={filters.q}
          onChange={(e) => onFiltersChange({ ...filters, q: e.target.value })}
          placeholder={t("intake.filters.search_ph")}
          className="h-8 rounded-[6px] text-[12.5px]"
        />
        <div className="flex gap-1.5">
          <Select value={filters.canal} onValueChange={(v) => onFiltersChange({ ...filters, canal: v })}>
            <SelectTrigger data-testid="intake-filter-canal" className="h-8 flex-1 rounded-[6px] text-[12px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("intake.filters.all_channels")}</SelectItem>
              {Object.keys(CHANNEL_ICON).map((c) => (
                <SelectItem key={c} value={c}>{t(`intake.channel.${c}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.estado} onValueChange={(v) => onFiltersChange({ ...filters, estado: v })}>
            <SelectTrigger data-testid="intake-filter-estado" className="h-8 flex-1 rounded-[6px] text-[12px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("intake.filters.all_statuses")}</SelectItem>
              {estados.map((e) => (
                <SelectItem key={e} value={e}>{t(`intake.status.${e}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-1" data-testid="intake-counters">
          {estados.map((e) => (
            <button
              key={e}
              data-testid={`intake-counter-${e}`}
              onClick={() => onFiltersChange({ ...filters, estado: e })}
              className="inline-flex items-center gap-1 rounded-[5px] border border-line px-1.5 py-[2px] text-[10.5px] text-ink-secondary hover:border-line-strong"
            >
              {t(`intake.status.${e}`)} <span className="tabular-nums font-semibold">{contadores?.[e] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" data-testid="intake-message-list">
        {loading ? (
          <div className="p-4 text-center text-[12px] text-ink-tertiary">{t("app.loading")}</div>
        ) : !items?.length ? (
          <EmptyState
            icon={MessageSquare}
            testId="intake-list-empty"
            title={t("intake.empty_title")}
            description={t("intake.empty_desc")}
          />
        ) : (
          items.map((m) => {
            const Icon = CHANNEL_ICON[m.canal] || MessageSquare;
            return (
              <button
                key={m.id}
                data-testid={`intake-message-row-${m.id}`}
                onClick={() => onSelect(m.id)}
                className={`block w-full border-b border-line px-3 py-2.5 text-left transition-colors ${
                  selectedId === m.id ? "bg-brand-soft" : "hover:bg-surface-sunken"
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <Icon className="h-3 w-3 shrink-0 text-ink-tertiary" />
                  <span className="truncate text-[12.5px] font-medium text-ink">
                    {m.remitente_nombre || m.remitente_contacto || t("intake.unknown_sender")}
                  </span>
                  {m.tiene_posible_duplicado && (
                    <AlertTriangle className="h-3 w-3 shrink-0 text-state-warn" data-testid={`intake-dup-flag-${m.id}`} />
                  )}
                  <span className="ml-auto shrink-0 text-[10.5px] text-ink-tertiary">{relativeTime(m.created_at)}</span>
                </div>
                <p className="mt-0.5 truncate text-[11.5px] text-ink-tertiary">{m.cuerpo}</p>
                <div className="mt-1">
                  <StatusChip tone={STATUS_TONE[m.estado_intake] || "gray"} label={t(`intake.status.${m.estado_intake}`)} />
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};
